import { BinIcon, BlockedIcon, CheckCircleIcon, MutedIcon, QuestionMarkIcon, SaveIcon, StoppedIcon, TimeIcon } from '@systemeio/ui/icons';
import { BreadcrumbsData, ContactSlugEnum, FormSelect, LocaleEnum, NeutralButton, NeutralTypeEnum, Popover, PrimaryButton, Tooltip, getLocaleLanguage, toast, useTranslation as useUiTranslation } from '@systemeio/ui';
import { BadRequest, InternalError } from '@systemeio/util';
import { deleteContact, updateContact } from 'modules/contacts/api/contact-api';
import { contactDefaultErrors } from 'modules/contacts/constants/contact-default-value-error';
import { changeContactMarketingSubscriptionStatus, changeContactTransactionalSubscriptionStatus, debounceContact } from 'modules/contacts/contact-page/api/contact-edit-api';
import ContactAddField from 'modules/contacts/contact-page/components/contact-add-field';
import ContactAutomationTasks from 'modules/contacts/contact-page/components/contact-automation-tasks';
import ContactCampaigns from 'modules/contacts/contact-page/components/contact-campaigns';
import ContactCommunities from 'modules/contacts/contact-page/components/contact-communities';
import ContactCourses from 'modules/contacts/contact-page/components/contact-courses';
import ContactEmails from 'modules/contacts/contact-page/components/contact-emails';
import ContactFieldSkeleton from 'modules/contacts/contact-page/components/contact-field-skeleton';
import ContactFields from 'modules/contacts/contact-page/components/contact-fields';
import ContactInfoSkeleton from 'modules/contacts/contact-page/components/contact-info-skeleton';
import { ContactNotes } from 'modules/contacts/contact-page/components/contact-notes';
import ContactPayments from 'modules/contacts/contact-page/components/contact-payments';
import ContactSelectTags from 'modules/contacts/contact-page/components/contact-select-tags';
import ContactSubscriptions from 'modules/contacts/contact-page/components/contact-subscriptions';
import ContactSurveys from 'modules/contacts/contact-page/components/contact-surveys';
import ContactTags from 'modules/contacts/contact-page/components/contact-tags';
import ContactWorkflows from 'modules/contacts/contact-page/components/contact-workflows';
import { ContactPageLoco } from 'modules/contacts/contact-page/constants/contact-page-loco';
import { useContact } from 'modules/contacts/contact-page/hooks/use-contact';
import { parseContactId } from 'modules/contacts/contact-page/utils/parse-contact-id';
import { useContactFields } from 'modules/contacts/hooks/use-contact-fields';
import { ContactErrorsInterface } from 'modules/contacts/types/contact-errors-interface';
import { ContactBulkApiInterface } from 'modules/contacts/types/contact-menu-interface';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import BreadcrumbsDashboard from 'shared/components/breadcrumbs';
import ConfirmationModal from 'shared/components/confirmation-modal';
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { LocaleDataInterface } from 'shared/types/locale-data-interface';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { pageTypeTitlesLocoKeys } from 'shared/enums/page-type-enum';
const ContactEdit: NextPage = () => {
  const router = useRouter();
  const {
    t
  } = useLocoTranslation();
  const {
    t: tUi
  } = useUiTranslation();
  const {
    user,
    checkAdminRoles,
    isWsAssistant,
    isSupportImpersonation
  } = useUser();
  const {
    contactId: queryContactId
  } = router.query;
  const contactId = parseContactId(queryContactId);
  const {
    fields,
    mutateFields
  } = useContactFields();
  const [isMarketingSubscribeModalOpen, setIsMarketingSubscribeModalOpen] = useState(false);
  const [isTransactionalSubscribeModalOpen, setIsTransactionalSubscribeModalOpen] = useState(false);
  const [isBouncedModalOpen, setIsBouncedModalOpen] = useState(false);
  const [errors, setErrors] = useState<ContactErrorsInterface>(contactDefaultErrors);
  const {
    data: contact,
    mutate
  } = useContact(contactId);
  const {
    revalidateModules
  } = useRevalidateModules();
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempState, setTempState] = useState<Record<string, string | undefined>>();
  const [tempTags, setTempTags] = useState<ContactBulkApiInterface[]>();
  useEffect(() => {
    if (contact) {
      setTempTags(contact.tags.map(tag => ({
        id: tag.id,
        name: tag.name
      })));
    }
  }, [contact]);
  const resetTags = () => setTempTags(() => (contact?.tags || []).map(tag_0 => ({
    id: tag_0.id,
    name: tag_0.name
  })));
  const [shouldUpdateTags, setShouldUpdateTags] = useState(false);
  useEffect(() => {
    if (fields && contact && user) {
      const fieldValues = fields.items.reduce((obj, el) => {
        const value = contact.fieldValues.find(contact_0 => contact_0.contactField.slug === el.slug)?.value;
        return {
          ...obj,
          [el.slug]: el.slug === ContactSlugEnum.Country ? value : value || ''
        };
      }, {});
      setTempState(prev => ({
        ...fieldValues,
        ['language']: contact.locale
      }));
    }
  }, [fields, contact, user]);
  const breadcrumbsData: BreadcrumbsData[] = [{
    caption: t('dashboard.contact_page.title_plural'),
    href: isWsAssistant ? '/assistant/contacts' : '/contacts',
    isStatic: true
  }, {
    caption: contact?.email || ''
  }];
  const localeData: LocaleDataInterface[] = Object.values(LocaleEnum).map(locale => ({
    id: locale as LocaleEnum,
    caption: getLocaleLanguage(locale, tUi)
  }));
  const changeMarketingSubscriptionStatus = async () => {
    if (contact) {
      await changeContactMarketingSubscriptionStatus(contact.id, contact.unsubscribed);
      await mutate(data => {
        if (data) {
          return {
            ...data,
            unsubscribed: !contact.unsubscribed
          };
        }
      }, false);
      await revalidateModules([RequestModuleEnum.Contacts]);
    }
  };
  const changeTransactionalSubscriptionStatus = async () => {
    if (contact) {
      await changeContactTransactionalSubscriptionStatus(contact.id);
      await mutate(data_0 => {
        if (data_0) {
          return {
            ...data_0,
            unsubscribedFromTransactionalEmails: !contact.unsubscribedFromTransactionalEmails
          };
        }
      }, false);
      await revalidateModules([RequestModuleEnum.Contacts]);
    }
  };
  const changeBouncedStatus = async () => {
    if (contact) {
      await debounceContact(contact.id);
      await mutate(data_1 => {
        if (data_1) {
          return {
            ...data_1,
            bounced: !contact.bounced
          };
        }
      }, false);
      await revalidateModules([RequestModuleEnum.Contacts]);
    }
  };
  const onDeleteContact = async () => {
    try {
      if (contact) {
        await deleteContact(contact.id);
        await revalidateModules([RequestModuleEnum.Contacts], true);
        isWsAssistant ? await router.replace('/assistant/contacts') : await router.replace('/contacts');
      }
    } catch (e) {
      toast.error(t('global.error'));
    }
  };
  const onUpdateContact = async () => {
    try {
      setErrors(contactDefaultErrors);
      if (fields && contact && tempState && tempTags) {
        setIsSaving(true);
        const response = await updateContact(contact.id, {
          fieldValues: fields.items.map(field => ({
            contactField: field.id,
            value: tempState[field.slug] ?? ''
          })),
          tags: tempTags.map(el_0 => el_0.id),
          locale: tempState['language'] as LocaleEnum
        });
        setShouldUpdateTags(true);
        await mutate(data_2 => {
          if (data_2) {
            return {
              ...data_2,
              ...response.data
            };
          }
        }, false);
        await revalidateModules([RequestModuleEnum.Contacts, RequestModuleEnum.Tags]);
        setIsSaving(false);
        setShouldUpdateTags(false);
        toast.success(t('dashboard.contact_page.updated'));
      }
    } catch (e_0) {
      setIsSaving(false);
      setShouldUpdateTags(false);
      if (e_0 instanceof BadRequest) {
        const contactErrors = e_0.errors;
        setErrors(prevState => ({
          ...prevState,
          fields: contactErrors.fields.fieldValues
        }));
        if (e_0.errors.common) {
          const common = contactErrors.common;
          setErrors(prevState_0 => ({
            ...prevState_0,
            common: common
          }));
          toast.error(common.join(''));
        }
      } else if (e_0 instanceof InternalError) {
        toast.error(t('core.error.internal_error_message'));
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  return <>
      <div className={`flex flex-col gap-5 lg:px-14 xl:px-28`}>
        <div className="flex items-center justify-between gap-5">
          <BreadcrumbsDashboard data={breadcrumbsData} isFetching={!contact} />
          <div className={'flex gap-3'}>
            <NeutralButton neutralType={NeutralTypeEnum.danger} onClick={() => setDeleteModalOpened(true)} disabled={!contact || isSaving}>
              <BinIcon className={'main-transition-colors group-hover:fill-danger fill-text-primary mb-0.5 h-[16px] w-[16px]'} />
              <span className={'hidden leading-4 lg:flex'}>{t('global.delete')}</span>
            </NeutralButton>
            <ConfirmationModal toastCaption={t('dashboard.contact_page.removed')} confirmationText={t('dashboard.contact_page.delete_confirmation')} opened={deleteModalOpened} onClose={() => setDeleteModalOpened(false)} onConfirm={onDeleteContact} />
            <PrimaryButton isFetching={isSaving} onClick={() => onUpdateContact()} disabled={!contact}>
              <SaveIcon />
              <span className={'hidden lg:flex'}>{t('global.save')}</span>
            </PrimaryButton>
          </div>
        </div>
        <div className={'bg-background-secondary flex flex-col gap-5 rounded-lg p-5'}>
          {contact ? <div className={'flex w-full flex-col gap-5 lg:flex-row'}>
              <div className={'flex w-full items-center gap-3 lg:w-1/2'}>
                <div className={'relative h-[80px] w-[80px] min-w-[80px] rounded-full'}>
                  <img className={'rounded-full'} src={contact.avatar} alt={'avatar'} />
                  {!contact.needConfirmation && <CheckCircleIcon className={'fill-green bg-background-secondary absolute inset-x-full inset-y-full -translate-x-full -translate-y-full rounded-full'} />}
                </div>
                <div className={'flex w-full flex-col truncate'}>
                  {contact.firstName && <span className={'text-text-primary mb-1 truncate text-[17px] font-bold'}>
                      {contact.firstName}
                    </span>}
                  <span className={'truncate font-semibold'}>{contact.email}</span>
                  <span className={'mb-1 truncate'}>
                    {`${t('dashboard.contact_page.affiliate_id')}: ${contact.affiliateAccount.affiliateId}`}
                  </span>
                  {contact.unsubscribed ? <div className={'flex w-full gap-1.5'}>
                      <Tooltip mode={'hover'} label={t('dashboard.contact_page.contact_silence')}>
                        <MutedIcon className={'main-transition-colors hover:fill-danger h-[20px] w-[20px]'} />
                      </Tooltip>
                      <button onClick={() => setIsMarketingSubscribeModalOpen(true)} className={'primary-link truncate outline-hidden'}>
                        {t('dashboard.contact_page.subscribe_again')}
                      </button>
                    </div> : <button onClick={() => setIsMarketingSubscribeModalOpen(true)} className={'primary-link w-fit truncate outline-hidden'}>
                      {t('dashboard.contact_page.actions.unsubscribe_from_marketing_email')}
                    </button>}
                  {contact.unsubscribedFromTransactionalEmails && <div className={'flex w-full gap-1.5'}>
                      <Tooltip mode={'hover'} label={t('dashboard.contact_page.unsubscribed_from_transactional_emails')}>
                        <BlockedIcon className={'main-transition-colors hover:fill-danger h-[16px] w-[16px]'} />
                      </Tooltip>
                      <button onClick={() => setIsTransactionalSubscribeModalOpen(true)} className={'primary-link truncate outline-hidden'}>
                        {t('dashboard.contact_page.resubscribe_to_transactional_emails')}
                      </button>
                    </div>}
                  {contact.bounced && <div className={'flex w-full gap-1.5'}>
                      <Tooltip mode={'hover'} label={t('dashboard.contact_page.contact_bounce', {
                  reason: contact.bounceReason
                })}>
                        <StoppedIcon className={'main-transition-colors hover:fill-danger h-[16px] w-[16px]'} />
                      </Tooltip>
                      {contact.bounceRemovePossible && <button onClick={() => setIsBouncedModalOpen(true)} className={'primary-link truncate outline-hidden'}>
                          {t('dashboard.contact_page.remove_bounced_mark')}
                        </button>}
                    </div>}
                  {contact.needConfirmation && <div className={'flex w-full gap-1.5'}>
                      <TimeIcon className={'main-transition-colors hover:fill-danger h-[16px] w-[16px]'} />
                      <span className={'truncate'}>
                        {t('dashboard.contact_page.not_confirmed')}
                      </span>
                    </div>}
                  <ConfirmationModal confirmationText={contact.unsubscribed ? t('dashboard.contact_page.subscribe_confirmation') : t('dashboard.contact_page.unsubscribe_confirmation')} opened={isMarketingSubscribeModalOpen} onClose={() => setIsMarketingSubscribeModalOpen(false)} onConfirm={changeMarketingSubscriptionStatus} />
                  <ConfirmationModal confirmationText={t('dashboard.contact_page.resubscribe_to_transactional_emails_confirmation')} opened={isTransactionalSubscribeModalOpen} onClose={() => setIsTransactionalSubscribeModalOpen(false)} onConfirm={changeTransactionalSubscriptionStatus} />
                  <ConfirmationModal title={t('dashboard.contact_page.remove_bounced_mark_title')} confirmationText={user && (checkAdminRoles() || isSupportImpersonation) ? t('dashboard.contact_page.remove_bounced_mark_confirmation_for_support') : t('dashboard.contact_page.remove_bounced_mark_confirmation')} opened={isBouncedModalOpen} onClose={() => setIsBouncedModalOpen(false)} onConfirm={changeBouncedStatus} />
                </div>
              </div>
              <div className={'ml-[92px] flex w-[calc(100%-92px)] flex-col gap-1 lg:ml-0 lg:w-1/2'}>
                <div className={'flex w-full gap-1.5'}>
                  <span className={'truncate'}>{`${t('dashboard.contact_page.added_on')}: ${dateTimeFormat(contact.registeredAt, {
                  timeFormat: user?.timeFormat,
                  dateFormat: user?.dateFormat,
                  timeZone: user?.timezone
                })}`}</span>
                  {contact.source && <Popover label={t(`dashboard.contact_page.source.source_${contact.source}`)}>
                      {show => <QuestionMarkIcon className={`${show ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
                    </Popover>}
                </div>
                {contact.httpReferer && <div className={'flex w-full gap-1'}>
                    <span className={'whitespace-nowrap'}>{`${t('dashboard.contact_page.source_url')}: `}</span>
                    <Tooltip wrapperClassName={'truncate'} labelClassName={'w-auto lg:max-w-[360px] break-all text-left'} mode={'hover'} stayOnHover label={contact.httpReferer}>
                      <a className={'primary-link block truncate outline-hidden'} href={contact.httpReferer} target={'_blank'} rel={'noreferrer'}>
                        {contact.httpReferer}
                      </a>
                    </Tooltip>
                  </div>}
                {contact.sourcePage && <div className={'flex w-full gap-1'}>
                    <span className={'whitespace-nowrap'}>{`${t(pageTypeTitlesLocoKeys[contact.sourcePage.type])}: `}</span>
                    <a className={'primary-link truncate outline-hidden'} href={contact.sourcePage.editUrl}>
                      {contact.sourcePage.name}
                    </a>
                  </div>}
                <div className={'flex w-full gap-1'}>
                  <span className={'whitespace-nowrap'}>{t('dashboard.user.affiliate')}: </span>
                  {contact.referredBy ? contact.referredBy.connectedUser ? <LinkWithoutPrefetch className={'primary-link truncate outline-hidden'} href={`/affiliate-program/${contact.referredBy.id}`}>
                        {contact.referredBy.email}
                      </LinkWithoutPrefetch> : <span className={'truncate'}>{contact.referredBy.email}</span> : <span className={'truncate'}>{t('global.none')}</span>}
                </div>
              </div>
            </div> : <ContactInfoSkeleton />}
          {tempState && fields ? <div className={'flex flex-col gap-5'}>
              <div className={'flex w-full flex-col flex-wrap items-end gap-y-5 lg:-ml-5 lg:w-[calc(100%+20px)] lg:flex-row'}>
                {fields.items.map((field_0, index) => <ContactFields type={'edit'} mutateFields={mutateFields} value={tempState[field_0.slug]} className={'w-full sm:basis-1/2 lg:pl-5'} key={field_0.id} field={field_0} onChange={value_0 => setTempState(prev_0 => ({
              ...prev_0,
              [field_0.slug]: value_0
            }))} error={errors?.fields && errors.fields[index] ? errors?.fields[index]?.value.join('') : ''} />)}
                <FormSelect withoutCloseIcon label={t('global.language')} placeholder={t('global.language')} containerClassName={'w-full sm:basis-1/2 lg:pl-5'} data={localeData} value={tempState['language'] as LocaleEnum} onChange={data_3 => setTempState(prev_1 => ({
              ...prev_1,
              ['language']: data_3
            }))} />
              </div>
              <ContactAddField disabled={!fields} mutateFields={mutateFields} />
            </div> : <ContactFieldSkeleton />}
          <ContactSelectTags resetTags={resetTags} tempTags={tempTags} setTempTags={setTempTags} />
          <FieldErrorAndDescription error={errors.common.join('')} errorClassName={'text-center'} />
        </div>
        <div className={'flex flex-col gap-5 lg:flex-row'}>
          <div className={'flex flex-col gap-5 lg:w-1/2'}>
            <div>
              <ContactSubscriptions contact={contact} contactId={contactId} />
            </div>
            <div>
              <ContactEmails contact={contact} contactId={contactId} />
            </div>
            <div>
              <ContactSurveys contact={contact} contactId={contactId} />
            </div>
            <div>
              <ContactCommunities contactId={contactId} />
            </div>
            <div>
              <ContactAutomationTasks contactId={contactId} contact={contact} />
            </div>
            <div>
              <ContactNotes contactId={contactId} />
            </div>
          </div>
          <div className={'flex flex-col gap-5 lg:w-1/2'}>
            <div>
              <ContactPayments contact={contact} contactId={contactId} />
            </div>
            <div>
              <ContactCampaigns contactId={contactId} />
            </div>
            <div>
              <ContactTags shouldUpdateTags={shouldUpdateTags} contact={contact} contactId={contactId} />
            </div>
            <div>
              <ContactCourses contactId={contactId} />
            </div>
            <div>
              <ContactWorkflows contactId={contactId} />
            </div>
          </div>
        </div>
      </div>
    </>;
};
export const getServerSideProps = getBaseServerSideProps(ContactPageLoco, 'header.top_menu.contacts');
export default ContactEdit;